import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { subMinutes, format, getUnixTime } from 'date-fns';
import { dropTask } from 'ember-concurrency';
import type AreaMapModel from 'garaje/models/area-map';
import type EmployeeModel from 'garaje/models/employee';
import type MapFeatureModel from 'garaje/models/map-feature';
import type MapFloorModel from 'garaje/models/map-floor';
import type ReservationModel from 'garaje/models/reservation';
import type AjaxService from 'garaje/services/ajax';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LiveMapService from 'garaje/services/live-map';
import { type ResourceState } from 'garaje/services/live-map';
import type StateService from 'garaje/services/state';
import type StoreService from 'garaje/services/store';
import type { Maybe } from 'graphql/jsutils/Maybe';
import type { Map } from 'leaflet';
import capitalize from 'lodash/capitalize';

interface ReservationModalResourcesMapArgs {
  selectedResourceType: string;
  selectedResourceFeatures: MapFeatureModel[];
  setSelectedResourceFeatures: (feature: MapFeatureModel, idx: number) => void;
  isEditing: boolean;
  currentReservation: {
    resourceId: string;
    resourceType: string;
  };
  selectedEmployee: EmployeeModel;
  selectedDates: Date[];
  selectedTime: { startTime: string; endTime: string };
  setCurrentPage: (page: string) => void;
  allDayEnabled: boolean;
  closeModal: () => void;
  setSuccessfulReservations: (reservations: ReservationModel[]) => void;
}

export default class ReservationModalResourcesMap extends Component<ReservationModalResourcesMapArgs> {
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare liveMap: LiveMapService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare flashMessages: FlashMessagesService;
  @service declare ajax: AjaxService;

  @tracked selectedDayIdx = 0;

  @tracked leafletMap: Maybe<Map> = null;
  @tracked areaMap: AreaMapModel | undefined;
  @tracked mapFloors: MapFloorModel[] = [];
  @tracked mapFloor: MapFloorModel | undefined;
  @tracked mapFeaturesForFloor: MapFeatureModel[] | undefined;
  @tracked resourceStates: ResourceState[] = [];

  constructor(owner: unknown, args: ReservationModalResourcesMapArgs) {
    super(owner, args);
    void this.initializeDataTask.perform();
  }

  initializeDataTask = dropTask(async () => {
    const { selectedDates } = this.args;
    if (selectedDates[0]) {
      await this.liveMap.getUpdatedLiveMapData({ selectedTime: getUnixTime(selectedDates[0]) });
    }
    await this.loadAreaMapTask.perform();
    this.setMapFeaturesForFloor();
  });

  loadAreaMapTask = dropTask(async () => {
    const { currentLocation } = this.state;
    const currentLocationId = currentLocation?.id;
    const areaMaps = await this.store.query('area-map', {
      filter: { location: currentLocationId },
      include: 'map-floors',
    });
    this.areaMap = areaMaps.slice()[0];
    this.mapFloors =
      this.store.peekAll('map-floor').filter((mapFloor) => {
        return mapFloor.belongsTo('areaMap').id() === this.areaMap?.id && mapFloor.rasterImageUrl;
      }) ?? [];
    if (this.args.currentReservation) {
      let selectedMapFeature = null;
      const mapFloor = this.mapFloors?.find((mapFloor) => {
        const featureIds = mapFloor.hasMany('mapFeatures').ids();
        const features = this.store.peekAll('map-feature').filter((feature) => featureIds.includes(feature.id));

        return features.some((feature: MapFeatureModel) => {
          if (feature.externalId == this.args.currentReservation.resourceId) {
            selectedMapFeature = feature;
            return true;
          }
          return false;
        });
      });
      if (selectedMapFeature) {
        this.args.setSelectedResourceFeatures(selectedMapFeature, this.selectedDayIdx);
        this.mapFloor = mapFloor;
        return;
      }
    }
    this.mapFloor = this.mapFloorsWithResource[0];
  });

  get mapFloorsWithResource(): MapFloorModel[] {
    return this.mapFloors.filter((mapFloor) => {
      const featureIds = mapFloor.hasMany('mapFeatures').ids();
      const features = this.store.peekAll('map-feature').filter((feature) => featureIds.includes(feature.id));
      return features.some((feature: MapFeatureModel) => feature.type === this.args.selectedResourceType);
    });
  }

  setMapFeaturesForFloor(): void {
    this.mapFeaturesForFloor = this.mapFloor?.mapFeatures.content;
  }

  get mapFeaturesToDisplay(): MapFeatureModel[] {
    const featuresToDisplay =
      this.mapFeaturesForFloor?.filter((feature) => feature.type === this.args.selectedResourceType) || [];
    return featuresToDisplay;
  }

  @action
  onFloorChange(selectedFloor: MapFloorModel): void {
    this.mapFloor = this.store.peekRecord('map-floor', selectedFloor.id) || undefined;
    this.setMapFeaturesForFloor();
  }

  @action
  onBack(): void {
    const { setCurrentPage } = this.args;
    setCurrentPage('employee');
  }

  get currentSelectedFeature(): MapFeatureModel | undefined {
    return this.args.selectedResourceFeatures[this.selectedDayIdx];
  }

  @action
  onSelectedFeatureChange(feature: MapFeatureModel): void {
    this.args.setSelectedResourceFeatures(feature, this.selectedDayIdx);
  }

  @action
  onNext(): void {
    this.selectedDayIdx += 1;
  }

  get selectedDay(): Date | undefined {
    return this.args.selectedDates[this.selectedDayIdx];
  }

  get selectedDayFormatted(): string {
    const localTime = subMinutes(
      this.selectedDay as Date,
      this.state.minutesBetweenTimezones(this.selectedDay as Date),
    );
    return format(localTime, 'EEEE, LLL dd, yyyy');
  }

  get resourceUnitText(): string {
    return capitalize(this.args.selectedResourceType);
  }

  get additionalDetails(): unknown {
    return {
      selectedResourceFilter: this.args.selectedResourceType,
      isDelegatedBooking: true,
    };
  }
}
